"use client";

import {
  useEffect,
  useRef,
  useState,
} from "react";
import {
  normalizeBuilderInlineTextPixelDraft,
} from "./inlineToolbarParity";

export type BuilderInlineToolbarPixelInputProps = {
  ariaLabel: string;
  max: number;
  min: number;
  onCommit: (value: string | undefined) => void;
  placeholder: string;
  value: string;
};

export function BuilderInlineToolbarPixelInput({
  ariaLabel,
  max,
  min,
  onCommit,
  placeholder,
  value,
}: BuilderInlineToolbarPixelInputProps) {
  const [draft, setDraft] = useState(value);
  const focusedRef = useRef(false);
  const escapedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(value);
    }
  }, [value]);

  function commitDraft() {
    const trimmed = draft.trim();

    if (trimmed === "") {
      if (value !== "") {
        onCommit(undefined);
        setDraft(value);
      }
      return;
    }

    const normalized = normalizeBuilderInlineTextPixelDraft(
      trimmed,
      min,
      max,
    );

    if (normalized === null) {
      setDraft(value);
      return;
    }

    setDraft(normalized);

    if (normalized !== value) {
      onCommit(`${normalized}px`);
    }
  }

  return (
    <input
      aria-label={ariaLabel}
      className="h-full min-w-0 flex-1 bg-transparent px-2 text-xs font-semibold text-slate-100 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      inputMode="decimal"
      max={max}
      min={min}
      placeholder={placeholder}
      step="0.5"
      type="number"
      value={draft}
      onBlur={() => {
        focusedRef.current = false;

        if (escapedRef.current) {
          escapedRef.current = false;
          return;
        }

        commitDraft();
      }}
      onChange={(event) => {
        setDraft(event.currentTarget.value);
      }}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          commitDraft();
          return;
        }

        if (event.key === "Escape") {
          event.preventDefault();
          escapedRef.current = true;
          setDraft(value);
          event.currentTarget.blur();
        }
      }}
    />
  );
}
